import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
} from "react";
import { Box } from "@mui/material";
import * as THREE from "three";
import { addPackingDimensionLabels } from "./dimensionLabels";

const MASTER_COLOR = 0x4285f4;
const PALLET_COLOR = 0xc47a2c;
const CONTAINER_COLOR = 0x2e7d32;
const DIAGONAL_COLOR = 0x1b5e20;

/**
 * Isometric Three.js view of a packed container.
 * Axes: X = width, Y = height (up), Z = length (front→back).
 *
 * @param {{ packPlan: import("./packing/types").PackPlan|null, containerIndex?: number }} props
 */
const EmpaqueScene = forwardRef(function EmpaqueScene(
  { packPlan, containerIndex = 0 },
  ref
) {
  const mountRef = useRef(null);
  const stateRef = useRef({
    renderer: null,
    scene: null,
    camera: null,
    contentGroup: null,
    animId: null,
    drag: { active: false, lastX: 0, lastY: 0, theta: Math.PI / 4, phi: Math.PI / 4 },
    target: new THREE.Vector3(),
    radius: 1,
  });

  useImperativeHandle(ref, () => ({
    capturePng() {
      const { renderer, scene, camera } = stateRef.current;
      if (!renderer || !scene || !camera) return null;
      renderer.render(scene, camera);
      return renderer.domElement.toDataURL("image/png");
    },
  }));

  // Init Three.js once
  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return undefined;

    const width = mount.clientWidth || 800;
    const height = mount.clientHeight || 520;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xf5f7f5);

    const aspect = width / height;
    const frustum = 200;
    const camera = new THREE.OrthographicCamera(
      (-frustum * aspect) / 2,
      (frustum * aspect) / 2,
      frustum / 2,
      -frustum / 2,
      0.1,
      100000
    );

    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      preserveDrawingBuffer: true,
    });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.setSize(width, height);
    mount.appendChild(renderer.domElement);

    const ambient = new THREE.AmbientLight(0xffffff, 0.75);
    scene.add(ambient);
    const dir = new THREE.DirectionalLight(0xffffff, 0.55);
    dir.position.set(1, 2, 1);
    scene.add(dir);

    const contentGroup = new THREE.Group();
    scene.add(contentGroup);

    stateRef.current = {
      ...stateRef.current,
      renderer,
      scene,
      camera,
      contentGroup,
    };

    const updateCamera = () => {
      const { drag, target, radius } = stateRef.current;
      const { theta, phi } = drag;
      camera.position.set(
        target.x + radius * Math.sin(phi) * Math.cos(theta),
        target.y + radius * Math.cos(phi),
        target.z + radius * Math.sin(phi) * Math.sin(theta)
      );
      camera.lookAt(target);
      camera.updateProjectionMatrix();
    };

    // Default isometric-ish angles
    stateRef.current.drag.theta = Math.PI / 4;
    stateRef.current.drag.phi = Math.PI / 4;
    updateCamera();

    const onPointerDown = (e) => {
      stateRef.current.drag.active = true;
      stateRef.current.drag.lastX = e.clientX;
      stateRef.current.drag.lastY = e.clientY;
    };
    const onPointerUp = () => {
      stateRef.current.drag.active = false;
    };
    const onPointerMove = (e) => {
      const d = stateRef.current.drag;
      if (!d.active) return;
      const dx = e.clientX - d.lastX;
      const dy = e.clientY - d.lastY;
      d.lastX = e.clientX;
      d.lastY = e.clientY;
      d.theta -= dx * 0.01;
      d.phi = Math.max(0.15, Math.min(Math.PI / 2 - 0.05, d.phi + dy * 0.01));
      updateCamera();
    };
    const onWheel = (e) => {
      e.preventDefault();
      const cam = stateRef.current.camera;
      if (!cam) return;
      const factor = e.deltaY > 0 ? 1.08 : 0.92;
      cam.zoom = Math.max(0.05, Math.min(20, cam.zoom * (1 / factor)));
      cam.updateProjectionMatrix();
    };

    renderer.domElement.addEventListener("pointerdown", onPointerDown);
    window.addEventListener("pointerup", onPointerUp);
    window.addEventListener("pointermove", onPointerMove);
    renderer.domElement.addEventListener("wheel", onWheel, { passive: false });

    const onResize = () => {
      if (!mountRef.current) return;
      const w = mountRef.current.clientWidth || 800;
      const h = mountRef.current.clientHeight || 520;
      const a = w / h;
      const f = stateRef.current.radius * 1.2 || 200;
      camera.left = (-f * a) / 2;
      camera.right = (f * a) / 2;
      camera.top = f / 2;
      camera.bottom = -f / 2;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    };
    window.addEventListener("resize", onResize);

    const animate = () => {
      stateRef.current.animId = requestAnimationFrame(animate);
      renderer.render(scene, camera);
    };
    animate();

    return () => {
      cancelAnimationFrame(stateRef.current.animId);
      window.removeEventListener("pointerup", onPointerUp);
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("resize", onResize);
      renderer.domElement.removeEventListener("pointerdown", onPointerDown);
      renderer.domElement.removeEventListener("wheel", onWheel);
      contentGroup.clear();
      renderer.dispose();
      if (renderer.domElement.parentNode === mount) {
        mount.removeChild(renderer.domElement);
      }
      stateRef.current.renderer = null;
    };
  }, []);

  // Rebuild mesh content when pack plan changes
  useEffect(() => {
    const { contentGroup, camera } = stateRef.current;
    if (!contentGroup || !camera) return;

    while (contentGroup.children.length) {
      const child = contentGroup.children[0];
      contentGroup.remove(child);
      child.traverse?.((obj) => {
        if (obj.geometry) obj.geometry.dispose();
        if (obj.material) {
          const mats = Array.isArray(obj.material)
            ? obj.material
            : [obj.material];
          mats.forEach((m) => {
            if (m.map) m.map.dispose();
            m.dispose();
          });
        }
      });
    }

    const container = packPlan?.containers?.[containerIndex];
    if (!container) return;

    const { contL, contW, contH } = container;
    const cx = contW / 2;
    const cy = contH / 2;
    const cz = contL / 2;

    // Container translucent shell (centered geometry, then positioned)
    const shellGeo = new THREE.BoxGeometry(contW, contH, contL);
    const shellMat = new THREE.MeshPhongMaterial({
      color: CONTAINER_COLOR,
      transparent: true,
      opacity: 0.12,
      depthWrite: false,
    });
    const shell = new THREE.Mesh(shellGeo, shellMat);
    shell.position.set(cx, cy, cz);
    contentGroup.add(shell);

    const edges = new THREE.EdgesGeometry(shellGeo);
    const line = new THREE.LineSegments(
      edges,
      new THREE.LineBasicMaterial({ color: CONTAINER_COLOR })
    );
    line.position.copy(shell.position);
    contentGroup.add(line);

    // Placed units — BoxGeometry is centered; shift by half extents
    for (const box of container.boxes || []) {
      const geo = new THREE.BoxGeometry(box.w, box.h, box.d);
      const mat = new THREE.MeshPhongMaterial({
        color: box.kind === "pallet" ? PALLET_COLOR : MASTER_COLOR,
        transparent: true,
        opacity: 0.88,
      });
      const mesh = new THREE.Mesh(geo, mat);
      mesh.position.set(box.x + box.w / 2, box.y + box.h / 2, box.z + box.d / 2);
      contentGroup.add(mesh);

      const boxEdges = new THREE.EdgesGeometry(geo);
      const boxLine = new THREE.LineSegments(
        boxEdges,
        new THREE.LineBasicMaterial({ color: 0x333333, transparent: true, opacity: 0.35 })
      );
      boxLine.position.copy(mesh.position);
      contentGroup.add(boxLine);
    }

    // Filler line: peak-rightmost master → container lower-right (length end, floor)
    if (container.diagonal) {
      const [z0, y0, z1, y1] = container.diagonal;
      const points = [
        new THREE.Vector3(cx, y0, z0),
        new THREE.Vector3(cx, y1, z1),
      ];
      const diagGeo = new THREE.BufferGeometry().setFromPoints(points);
      const diagLine = new THREE.Line(
        diagGeo,
        new THREE.LineBasicMaterial({ color: DIAGONAL_COLOR, linewidth: 2 })
      );
      contentGroup.add(diagLine);
      // Tube overlay so the line stays visible regardless of WebGL linewidth limits
      const curve = new THREE.LineCurve3(points[0], points[1]);
      const tube = new THREE.Mesh(
        new THREE.TubeGeometry(curve, 8, Math.max(contL, contW, contH) * 0.004, 6, false),
        new THREE.MeshBasicMaterial({ color: DIAGONAL_COLOR })
      );
      contentGroup.add(tube);
    }

    // Red dimension labels: axis totals + per-step width/height (box counts)
    addPackingDimensionLabels(contentGroup, container);

    // Frame camera on container
    const maxDim = Math.max(contL, contW, contH) || 1;
    stateRef.current.target.set(cx, cy, cz);
    stateRef.current.radius = maxDim * 1.8;

    const aspect =
      (mountRef.current?.clientWidth || 800) /
      (mountRef.current?.clientHeight || 520);
    const f = maxDim * 1.4;
    camera.left = (-f * aspect) / 2;
    camera.right = (f * aspect) / 2;
    camera.top = f / 2;
    camera.bottom = -f / 2;
    camera.zoom = 1;
    camera.updateProjectionMatrix();

    const { drag, target, radius } = stateRef.current;
    camera.position.set(
      target.x + radius * Math.sin(drag.phi) * Math.cos(drag.theta),
      target.y + radius * Math.cos(drag.phi),
      target.z + radius * Math.sin(drag.phi) * Math.sin(drag.theta)
    );
    camera.lookAt(target);
  }, [packPlan, containerIndex]);

  return (
    <Box
      ref={mountRef}
      sx={{
        width: "100%",
        height: 520,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
        overflow: "hidden",
        bgcolor: "#f5f7f5",
        cursor: "grab",
      }}
    />
  );
});

export default EmpaqueScene;
